"use client";

import { c as _c } from "react/compiler-runtime";
import { formatCurrency } from "@/lib/formatting";
import { useEffect, useRef } from "react";
import { X, MapPin } from "lucide-react";
import { useTenant } from "@/lib/tenant";
import type { DealWithRelations } from "@/lib/deals/types";
import type { DealPerspective } from "@/lib/deals/status-config";
import { DealChat } from "@/components/deals/DealChat";
import { dealTotal } from "@/lib/deals/positions";
import { dealPriceLabel, dealQuantityLabel } from "@/lib/deals/position-labels";

// --- Helpers (same as UnifiedDealCard) ---

function getLocation(deal: DealWithRelations, perspective: DealPerspective): string {
  if (perspective === "buyer") {
    const {
      landkreis,
      bundesland
    } = deal.offering ?? {};
    if (landkreis && bundesland) return `${landkreis}, ${bundesland}`;
    return bundesland || "Deutschland";
  }
  const {
    landkreis,
    bundesland
  } = deal.request ?? {};
  if (landkreis && bundesland) return `${landkreis}, ${bundesland}`;
  return bundesland || "";
}

// --- Component ---

interface DealChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  deal: DealWithRelations;
  perspective: DealPerspective;
  userId: string;
  dealNumber?: number;
}
export function DealChatModal(t0) {
  const $ = _c(48);
  const {
    isOpen,
    onClose,
    deal,
    perspective,
    userId
  } = t0;
  useTenant();
  const modalRef = useRef(null);
  let t1;
  if ($[0] !== deal || $[1] !== perspective) {
    t1 = getLocation(deal, perspective);
    $[0] = deal;
    $[1] = perspective;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const location = t1;
  let t2;
  if ($[3] !== deal.positions) {
    t2 = deal.positions ?? [];
    $[3] = deal.positions;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = dealQuantityLabel(t2);
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const quantity = t3;
  let t4;
  if ($[7] !== deal.positions) {
    t4 = deal.positions ?? [];
    $[7] = deal.positions;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = dealPriceLabel(t4);
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const price = t5;
  const total = dealTotal(deal.positions ?? []);
  let t6;
  if ($[11] !== deal.id || $[12] !== deal.offering) {
    t6 = deal.offering?.bundesland ? `Vorgang – ${deal.offering.bundesland}` : `Vorgang ${deal.id.slice(0, 8)}`;
    $[11] = deal.id;
    $[12] = deal.offering;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const dealTitle = t6;
  let t7;
  let t8;
  if ($[14] !== isOpen || $[15] !== onClose) {
    t7 = () => {
      const handleEscape = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
        document.body.style.overflow = "hidden";
      }
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "";
      };
    };
    t8 = [isOpen, onClose];
    $[14] = isOpen;
    $[15] = onClose;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  useEffect(t7, t8);
  let t9;
  if ($[18] !== onClose) {
    t9 = e_0 => {
      if (modalRef.current && !modalRef.current.contains(e_0.target as Node)) {
        onClose();
      }
    };
    $[18] = onClose;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handleBackdropClick = t9;
  if (!isOpen) {
    return null;
  }
  const t10 = "fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200";
  const t11 = "dialog";
  const t12 = "true";
  const t13 = "deal-chat-modal-title";
  const t14 = "relative w-full max-w-4xl mx-4 max-h-[90vh] bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 flex flex-col overflow-hidden";
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <h2 id="deal-chat-modal-title" className="text-sm font-semibold text-zinc-900">Nachricht</h2>;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <X className="w-3.5 h-3.5" />;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== onClose) {
    t17 = <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 flex-shrink-0">{t15}<button onClick={onClose} className="flex items-center gap-1.5 px-2 py-1 text-xs text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors" aria-label="Schliessen">{t16}Schliessen</button></div>;
    $[22] = onClose;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  const t18 = "flex-1 flex flex-col lg:flex-row overflow-hidden";
  const t19 = "lg:w-64 flex-shrink-0 border-b lg:border-b-0 border-zinc-100 p-4 overflow-y-auto";
  const t20 = "space-y-2.5 text-xs";
  let t21;
  if ($[24] !== quantity) {
    t21 = quantity && <div><p className="text-zinc-400">Menge</p><p className="font-medium text-zinc-800">{quantity}</p></div>;
    $[24] = quantity;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] !== price) {
    t22 = price && <div><p className="text-zinc-400">Preis</p><p className="font-medium text-zinc-800">{price}</p></div>;
    $[26] = price;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  const t23 = total != null && <div><p className="text-zinc-400">Gesamtwert</p><p className="font-semibold text-primary">{formatCurrency(total)}</p></div>;
  let t24;
  if ($[28] !== location) {
    t24 = location && <div><p className="text-zinc-400">Standort</p><p className="font-medium text-zinc-800 flex items-center gap-1"><MapPin className="w-3 h-3 text-zinc-400" />{location}</p></div>;
    $[28] = location;
    $[29] = t24;
  } else {
    t24 = $[29];
  }
  let t25;
  if ($[30] !== t21 || $[31] !== t22 || $[32] !== t23 || $[33] !== t24) {
    t25 = <div className={t19}><div className={t20}>{t21}{t22}{t23}{t24}</div></div>;
    $[30] = t21;
    $[31] = t22;
    $[32] = t23;
    $[33] = t24;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  let t26;
  if ($[35] !== deal.id || $[36] !== dealTitle || $[37] !== userId) {
    t26 = <div className="flex-1 overflow-hidden p-2"><DealChat dealId={deal.id} dealTitle={dealTitle} currentUserId={userId} /></div>;
    $[35] = deal.id;
    $[36] = dealTitle;
    $[37] = userId;
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  let t27;
  if ($[39] !== t25 || $[40] !== t26) {
    t27 = <div className={t18}>{t25}{t26}</div>;
    $[39] = t25;
    $[40] = t26;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  let t28;
  if ($[42] !== t17 || $[43] !== t27) {
    t28 = <div ref={modalRef} className={t14}>{t17}{t27}</div>;
    $[42] = t17;
    $[43] = t27;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] !== handleBackdropClick || $[46] !== t28) {
    t29 = <div className={t10} onClick={handleBackdropClick} role={t11} aria-modal={t12} aria-labelledby={t13}>{t28}</div>;
    $[45] = handleBackdropClick;
    $[46] = t28;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  return t29;
}